import { OfflineConfigSettings } from "../../services/offline-data-service/OfflineDataService";
import { DataTooltipDisplayEnum, IUserSettings, MarketingReportPreferenceSortDateEnum, TagHistoryViewEnum, UIShortcutEnum } from "../../services/user-settings-data/Models";
import i18n from "../../services/localization-service/i18n";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import { IKeyValuePair } from "../../common/models/KeyValuePair";
import { GaugeSheetViewMode } from "../../views/gauge-sheet-view/GaugeSheetState";

export type AccountSettingsV2FormType = {
    pageSize: number;
    offlineMode: boolean;
    functionIcons: boolean;
    alarmsAtTop: boolean;
    outletsFirst: boolean;
    sortDates: MarketingReportPreferenceSortDateEnum;
    graphTooltip: DataTooltipDisplayEnum;
    graphsInViewPort: number;
    deviceView: boolean;
    tagView: TagHistoryViewEnum;
    theme: string;

    collapseTagGroups: number;

    defaultDeviceView: UIViewEnum;
    defaultGroupView: UIViewEnum;
    gridFontSize: number;
    gridCellPadding: number;


    gaugeSheetViewMode: GaugeSheetViewMode;
    gaugeSheetShowDevice: boolean;
    gaugeSheetShowTanks: boolean;

    tankLevelInches: number;


    historicalData: boolean;
    graph: boolean;
    marketingReport: boolean;
    plungerAnalysis: boolean;
    gaugeSheet: boolean;
    dynacard: boolean;
    addMemo: boolean;
    mapIt: boolean;


}

export const defaultAccountSettingsV2: AccountSettingsV2FormType = {
        pageSize: 50,
        offlineMode: false,
        functionIcons: true,
        alarmsAtTop: true,
        outletsFirst: false,
        sortDates: MarketingReportPreferenceSortDateEnum.LeftToRight,
        graphTooltip: DataTooltipDisplayEnum.Shared,
        graphsInViewPort: 2,
        deviceView: true,
        tagView: TagHistoryViewEnum.Chart,
        theme: 'light',
        collapseTagGroups: 0,
        defaultDeviceView: UIViewEnum.None,
        defaultGroupView: UIViewEnum.None,
        gridFontSize: 13,
        gridCellPadding: 5,
        gaugeSheetViewMode: GaugeSheetViewMode.Spreadsheet,
        gaugeSheetShowDevice: true,
        gaugeSheetShowTanks: true,
        tankLevelInches: 0,

        historicalData: false,
        graph: false,
        marketingReport: false,
        plungerAnalysis: false,
        gaugeSheet: false,
        dynacard: false,
        addMemo: false,
        mapIt: false,
}

export const groupViews: IKeyValuePair<string, UIViewEnum>[] = [
    { Key: i18n.t('views.customForms'), Value: UIViewEnum.Group_CustomForm},
    { Key: i18n.t('views.dashboard'), Value: UIViewEnum.Group_Dashboard},
    { Key: i18n.t('views.groupGraph'), Value: UIViewEnum.Group_Graph },
    { Key: i18n.t('views.imageView'), Value: UIViewEnum.Group_HMIView },
    { Key: i18n.t('views.groupManualEntry'), Value: UIViewEnum.Group_ManualEntry },
    { Key: i18n.t('views.map'), Value: UIViewEnum.Group_MapView },
    { Key: i18n.t('views.reporting'), Value: UIViewEnum.Group_Reporting },
    { Key: i18n.t('views.summaryListing'), Value: UIViewEnum.Group_SummaryListing },
];

export const deviceViews: IKeyValuePair<string, UIViewEnum>[] = [
    { Key: i18n.t('views.dashboard'), Value: UIViewEnum.Device_Dashboard },
    { Key: i18n.t('views.deviceDetails'), Value: UIViewEnum.Device_Details },
    { Key: i18n.t('views.deviceGraph'), Value: UIViewEnum.Device_Graph },
    { Key: i18n.t('views.manualEntry'), Value: UIViewEnum.Device_ManualEntry },
    { Key: i18n.t('views.customForms'), Value: UIViewEnum.Device_CustomForm },
    { Key: i18n.t('views.imageView'), Value: UIViewEnum.Device_HMIView },
];


export type DirtyFieldsType = Partial<Readonly< {
    pageSize?: boolean | undefined;
    offlineMode?: boolean | undefined;
    functionIcons?: boolean | undefined;
    alarmsAtTop?: boolean | undefined;
    outletsFirst?: boolean | undefined;
    sortDates?: boolean | undefined;
    graphTooltip?: boolean | undefined;
    graphsInViewPort?: boolean | undefined;
    deviceView?: boolean | undefined;
    tagView?: boolean | undefined;
    theme?: boolean | undefined;

    collapseTagGroups?: boolean | undefined;

    defaultDeviceView?: boolean | undefined;
    defaultGroupView?: boolean | undefined;

    gridFontSize?: boolean | undefined;
    gridCellPadding?: boolean | undefined;

    gaugeSheetViewMode?: boolean | undefined;
    gaugeSheetShowDevice?: boolean | undefined;
    gaugeSheetShowTanks?: boolean | undefined;

    tankLevelInches?: boolean | undefined;

    historicalData?: boolean | undefined;
    graph?: boolean | undefined;
    marketingReport?: boolean | undefined;
    plungerAnalysis?: boolean | undefined;
    gaugeSheet?: boolean | undefined;
    dynacard?: boolean | undefined;
    addMemo?: boolean | undefined;
    mapIt?: boolean | undefined;

}>>;

const AccountSettingsV2Utils = {

    mapToForm: function(
        userSettings: IUserSettings | undefined,
        offlineConfig: OfflineConfigSettings,
        defaultDeviceView: UIViewEnum,
        defaultGroupView: UIViewEnum,
        ): AccountSettingsV2FormType {

        const mask = userSettings?.uiShortcutMask ?? 0;

        const form: AccountSettingsV2FormType = {

            pageSize: userSettings ? userSettings.gridRowCount : 0,
            offlineMode: offlineConfig.enabled ?? false,
            functionIcons: userSettings ? userSettings.summaryListingDisplayFunctionIcons : false,
            alarmsAtTop: userSettings ? userSettings.summaryListingAlarmAtTop : false,
            outletsFirst: userSettings ? userSettings.summaryListingOutletsFirst : false,
            sortDates: userSettings ? userSettings.marketingReportSortDate : MarketingReportPreferenceSortDateEnum.LeftToRight,
            graphTooltip: userSettings ? userSettings.trendingDataTooltipDisplay : DataTooltipDisplayEnum.Separate,
            graphsInViewPort: userSettings ? userSettings.trendingNumberChartsInViewport : 1,
            deviceView: userSettings ? userSettings.defaultDeviceView : false,
            tagView: userSettings ? userSettings.tagHistoryView : TagHistoryViewEnum.Grid,
            theme: userSettings ? userSettings.theme ?? 'light' : 'light',
            collapseTagGroups: userSettings ? userSettings.collapseTagGroups ? 1 : 0 : 0,
            defaultDeviceView: defaultDeviceView,
            defaultGroupView: defaultGroupView,

            gridFontSize: userSettings?.gridFontSize ?? 13,
            gridCellPadding: userSettings?.gridCellPadding ?? 5,

            gaugeSheetViewMode: userSettings?.gaugeSheetViewMode ?? GaugeSheetViewMode.Spreadsheet,
            gaugeSheetShowDevice: userSettings?.gaugeSheetShowDevice ?? true,
            gaugeSheetShowTanks: userSettings?.gaugeSheetShowTanks ?? true,

            tankLevelInches: userSettings?.tankLevelInches ? 1 : 0,

            historicalData: (mask & UIShortcutEnum.HistoricalData) > 0,
            graph:  (mask & UIShortcutEnum.Graph) > 0,
            marketingReport:  (mask & UIShortcutEnum.MarketingReport) > 0,
            plungerAnalysis:  (mask & UIShortcutEnum.PlungerAnalysis) > 0,
            gaugeSheet:  (mask & UIShortcutEnum.GaugeSheet) > 0,
            dynacard:  (mask & UIShortcutEnum.Dynacard) > 0,
            addMemo:  (mask & UIShortcutEnum.AddMemo) > 0,
            mapIt:  (mask & UIShortcutEnum.MapIt) > 0
        }

        return form;
    },
    
    hasPreferenceChanges: function(dirtyFields: DirtyFieldsType): boolean {

        return ((dirtyFields.pageSize ?? false) ||
        (dirtyFields.offlineMode ?? false) ||
        (dirtyFields.functionIcons ?? false) ||
        (dirtyFields.alarmsAtTop ?? false) ||
        (dirtyFields.outletsFirst ?? false) ||
        (dirtyFields.sortDates ?? false) ||
        (dirtyFields.graphTooltip ?? false) ||
        (dirtyFields.graphsInViewPort ?? false) ||
        (dirtyFields.deviceView ?? false) ||
        (dirtyFields.tagView ?? false) ||
        (dirtyFields.theme ?? false)) ||
        ((dirtyFields.collapseTagGroups ?? false)) ||
        ((dirtyFields.defaultDeviceView ?? false)) ||
        ((dirtyFields.defaultGroupView ?? false)) ||
        (dirtyFields.gridFontSize ?? false) ||
        (dirtyFields.gridCellPadding ?? false) ||
        (dirtyFields.gaugeSheetViewMode ?? false) ||
        (dirtyFields.gaugeSheetShowDevice ?? false) ||
        (dirtyFields.gaugeSheetShowTanks ?? false) ||
        (dirtyFields.tankLevelInches ?? false) ||
        (dirtyFields.historicalData ?? false) ||
        (dirtyFields.graph ?? false) ||
        (dirtyFields.marketingReport ?? false) ||
        (dirtyFields.plungerAnalysis ?? false) ||
        (dirtyFields.gaugeSheet ?? false) ||
        (dirtyFields.dynacard ?? false) ||
        (dirtyFields.addMemo ?? false) ||
        (dirtyFields.mapIt ?? false)

    },
    mapToPreferencesModel: function(form: AccountSettingsV2FormType, userSettings: IUserSettings): IUserSettings {


        let uiShortCuts = 0;
        if (form.historicalData) {
            uiShortCuts |= UIShortcutEnum.HistoricalData;
        }

        if (form.graph) {
            uiShortCuts |= UIShortcutEnum.Graph;
        }
        if (form.marketingReport) {
            uiShortCuts |= UIShortcutEnum.MarketingReport;
        }
        if (form.plungerAnalysis) {
            uiShortCuts |= UIShortcutEnum.PlungerAnalysis;
        }
        if (form.gaugeSheet) {
            uiShortCuts |= UIShortcutEnum.GaugeSheet;
        }
        if (form.dynacard) {
            uiShortCuts |= UIShortcutEnum.Dynacard;
        }
        if (form.addMemo) {
            uiShortCuts |= UIShortcutEnum.AddMemo;
        }
        if (form.mapIt) {
            uiShortCuts |= UIShortcutEnum.MapIt;
        }

        form.functionIcons = false;


        const settings: IUserSettings = {
            ...userSettings,
            gridRowCount: form.pageSize,
            summaryListingDisplayFunctionIcons: form.functionIcons,
            summaryListingAlarmAtTop: form.alarmsAtTop,
            summaryListingOutletsFirst: form.outletsFirst,
            marketingReportSortDate: form.sortDates,
            trendingDataTooltipDisplay: form.graphTooltip,
            trendingNumberChartsInViewport: form.graphsInViewPort,
            defaultDeviceView: form.deviceView,
            tagHistoryView: form.tagView,
            theme: form.theme,
            collapseTagGroups: form.collapseTagGroups === 1,
            gridFontSize: form.gridFontSize,
            gridCellPadding: form.gridCellPadding,  
            gaugeSheetViewMode: form.gaugeSheetViewMode,
            gaugeSheetShowDevice: form.gaugeSheetShowDevice,
            gaugeSheetShowTanks: form.gaugeSheetShowTanks,

            tankLevelInches: form.tankLevelInches !== 0,
            uiShortcutMask: uiShortCuts
        };

        return settings;
    }
}

export default AccountSettingsV2Utils;